/** Custom Store Groups cross-frame listeners: forward the named request fields to @api/storeGroups. */

import {
    listStoreGroups,
    getStoreGroupInfo,
    listStoreGroupLeafStores,
    createStoreGroup,
    updateStoreGroup,
    nestStoreGroup,
    detachStoreGroup,
    countStoreGroupGrants,
    deleteStoreGroup,
    IListStoreGroupsInput,
    IGetStoreGroupInfoInput,
    IListStoreGroupLeafStoresInput,
    ICreateStoreGroupInput,
    IUpdateStoreGroupInput,
    INestStoreGroupInput,
    IDetachStoreGroupInput,
    ICountStoreGroupGrantsInput,
    IDeleteStoreGroupInput,
} from '@api/storeGroups'
import type {
    ListStoreGroupsOutput,
    GetStoreGroupInfoOutput,
    ListStoreGroupLeafStoresOutput,
    CreateStoreGroupOutput,
    UpdateStoreGroupOutput,
    NestStoreGroupOutput,
    DetachStoreGroupOutput,
    GetStoreGroupGrantCountOutput,
    DeleteStoreGroupOutput,
} from '@amzn/coral_com-amazon-jwostoremanagementgateway'

/** A cross-frame request whose `data` carries (some of) the wrapper's input. */
interface IStoreGroupRequest<T> { data?: Partial<T> }

export async function listStoreGroupsListener (message: IStoreGroupRequest<IListStoreGroupsInput>): Promise<ListStoreGroupsOutput> {
    const params: Partial<IListStoreGroupsInput> = message?.data || {}
    return listStoreGroups({
        organizationId: params.organizationId,
        nameContains: params.nameContains,
        kind: params.kind,
        maxResults: params.maxResults,
        nextToken: params.nextToken,
    })
}

export async function getStoreGroupInfoListener (message: IStoreGroupRequest<IGetStoreGroupInfoInput>): Promise<GetStoreGroupInfoOutput> {
    const params: Partial<IGetStoreGroupInfoInput> = message?.data || {}
    return getStoreGroupInfo({ organizationId: params.organizationId, groupId: params.groupId })
}

export async function listStoreGroupLeafStoresListener (message: IStoreGroupRequest<IListStoreGroupLeafStoresInput>): Promise<ListStoreGroupLeafStoresOutput> {
    const params: Partial<IListStoreGroupLeafStoresInput> = message?.data || {}
    return listStoreGroupLeafStores({
        organizationId: params.organizationId,
        groupIds: params.groupIds,
        maxResults: params.maxResults,
        nextToken: params.nextToken,
    })
}

export async function createStoreGroupListener (message: IStoreGroupRequest<ICreateStoreGroupInput>): Promise<CreateStoreGroupOutput> {
    const params: Partial<ICreateStoreGroupInput> = message?.data || {}
    return createStoreGroup({
        organizationId: params.organizationId,
        name: params.name,
        kind: params.kind,
        memberStoreIds: params.memberStoreIds,
        memberGroupIds: params.memberGroupIds,
    })
}

export async function updateStoreGroupListener (message: IStoreGroupRequest<IUpdateStoreGroupInput>): Promise<UpdateStoreGroupOutput> {
    const params: Partial<IUpdateStoreGroupInput> = message?.data || {}
    return updateStoreGroup({
        organizationId: params.organizationId,
        groupId: params.groupId,
        expectedVersion: params.expectedVersion,
        name: params.name,
        addStoreIds: params.addStoreIds,
        removeStoreIds: params.removeStoreIds,
    })
}

export async function nestStoreGroupListener (message: IStoreGroupRequest<INestStoreGroupInput>): Promise<NestStoreGroupOutput> {
    const params: Partial<INestStoreGroupInput> = message?.data || {}
    return nestStoreGroup({
        organizationId: params.organizationId,
        groupId: params.groupId,
        childGroupId: params.childGroupId,
        expectedParentVersion: params.expectedParentVersion,
    })
}

export async function detachStoreGroupListener (message: IStoreGroupRequest<IDetachStoreGroupInput>): Promise<DetachStoreGroupOutput> {
    const params: Partial<IDetachStoreGroupInput> = message?.data || {}
    return detachStoreGroup({
        organizationId: params.organizationId,
        groupId: params.groupId,
        childGroupId: params.childGroupId,
        expectedParentVersion: params.expectedParentVersion,
    })
}

export async function countStoreGroupGrantsListener (message: IStoreGroupRequest<ICountStoreGroupGrantsInput>): Promise<GetStoreGroupGrantCountOutput> {
    const params: Partial<ICountStoreGroupGrantsInput> = message?.data || {}
    return countStoreGroupGrants({ organizationId: params.organizationId, groupId: params.groupId })
}

export async function deleteStoreGroupListener (message: IStoreGroupRequest<IDeleteStoreGroupInput>): Promise<DeleteStoreGroupOutput> {
    const params: Partial<IDeleteStoreGroupInput> = message?.data || {}
    return deleteStoreGroup({
        organizationId: params.organizationId,
        groupId: params.groupId,
        expectedVersion: params.expectedVersion,
    })
}
